import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Play, BadgeCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Live Preview modal — shown when a visitor clicks "Watch Demo" on a
 * product in the "Try Before You Buy" section. Fetches a real sample
 * (Custom GPT instructions excerpt + use cases + features + 1 FAQ) so
 * the buyer can verify the value before they pay.
 *
 * For products with a real `video_demo_url`, embeds the video instead.
 */
export default function LivePreviewModal({ productSlug, onClose, onAddToCart }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/products/${productSlug}/preview`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setData({ error: e?.response?.data?.detail || 'Preview unavailable' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productSlug]);

  if (!productSlug) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="live-preview-overlay"
    >
      <div
        className="relative w-full max-w-3xl bg-neutral-900 border border-white/10 rounded-2xl my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="live-preview-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-black/40 hover:bg-black/70 flex items-center justify-center"
          aria-label="Close preview"
          data-testid="live-preview-close"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {loading && (
          <div className="p-16 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
          </div>
        )}

        {!loading && data?.error && (
          <div className="p-12 text-center">
            <p className="text-rose-400 mb-3">Preview unavailable</p>
            <p className="text-neutral-500 text-sm">{data.error}</p>
          </div>
        )}

        {!loading && data && !data.error && (
          <>
            {/* Hero */}
            <div className="relative aspect-[2/1] bg-neutral-800 rounded-t-2xl overflow-hidden">
              {data.image && (
                <img src={data.image} alt={data.name} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 to-transparent" />
              <div className="absolute bottom-4 left-6 right-6">
                <div className="inline-flex items-center gap-1.5 bg-[#00F0FF]/20 backdrop-blur border border-[#00F0FF]/40 text-[#00F0FF] text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-full mb-2">
                  <Play className="w-3 h-3" /> Live Preview
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">{data.name}</h2>
                {data.tagline && <p className="text-neutral-300 text-sm mt-1">{data.tagline}</p>}
              </div>
            </div>

            {/* Body */}
            <div className="p-6 md:p-8 space-y-5">
              {data.video_demo_url && (
                <div className="aspect-video rounded-xl overflow-hidden bg-black">
                  <iframe
                    src={data.video_demo_url}
                    title={`${data.name} demo`}
                    className="w-full h-full"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                  />
                </div>
              )}

              {data.sample_prompts && data.sample_prompts.length > 0 && (
                <div data-testid="live-preview-sample-prompts">
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-bold mb-2">
                    {data.sample_prompts[0].title}
                  </div>
                  <pre className="bg-black/40 border border-white/5 rounded-lg p-4 text-xs text-neutral-300 whitespace-pre-wrap font-mono leading-relaxed max-h-72 overflow-y-auto">
                    {data.sample_prompts[0].body}
                  </pre>
                  <p className="text-[11px] text-neutral-500 mt-2">
                    This is a preview excerpt — the full pack is delivered instantly on purchase.
                  </p>
                </div>
              )}

              {data.features && data.features.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-bold mb-2">
                    What's inside
                  </div>
                  <ul className="space-y-1.5">
                    {data.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-neutral-300">
                        <BadgeCheck className="w-4 h-4 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data.use_cases && data.use_cases.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-bold mb-2">
                    Made for
                  </div>
                  <ul className="space-y-1 text-sm text-neutral-400">
                    {data.use_cases.map((u, i) => <li key={i}>• {u}</li>)}
                  </ul>
                </div>
              )}

              {data.faq && data.faq.length > 0 && (
                <div className="border-t border-white/5 pt-4">
                  <div className="text-sm font-semibold text-white">{data.faq[0].q}</div>
                  <div className="text-sm text-neutral-400 mt-1">{data.faq[0].a}</div>
                </div>
              )}

              {/* Action bar */}
              <div className="flex items-center justify-between gap-3 border-t border-white/5 pt-5">
                <span className="text-2xl font-bold text-[#00F0FF]">
                  ${data.price}
                  {data.is_subscription ? <span className="text-sm text-neutral-400">/mo</span> : ''}
                </span>
                <Button
                  onClick={() => { onAddToCart?.(data); onClose?.(); }}
                  className="bg-white hover:bg-neutral-200 text-black rounded-full text-sm"
                  data-testid="live-preview-add-to-cart"
                >
                  Add to Cart
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
